import { ConfigService } from "./../config-service/config.service";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ChecklistQuestion } from "@stockcare-models/ChecklistQuestion";
import { WhoChecklist } from "@stockcare-models/WhoChecklist";
import { QueueType } from "@stockcare-models/QueueType";
import { QueueService } from "@stockcare-services/queue-service/queue.service";
import { ChecklistProgressStorageService } from "@stockcare-storage/checklist/checklist-progress-storage.service";
import { ChecklistQuestionStorageService } from "@stockcare-storage/checklist/checklist-question-storage.service";
import { WhoChecklistStorageService } from "@stockcare-storage/checklist/who-checklist-storage.service";
import { Observable, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class WhoChecklistService {

  constructor(
    private http: HttpClient,
    private configurationService: ConfigService,
    private checklistProgressStorageService: ChecklistProgressStorageService,
    private checklistQuestionStorageService: ChecklistQuestionStorageService,
    private whoChecklistStorageService: WhoChecklistStorageService,
    private queueService: QueueService
  ) {
  }

  getChecklistQuestionText(fromDate: Date, toDate: Date, institutionId: number, physicalLocationId: number) {

    var obj = {
      fromDate: fromDate,
      toDate: toDate,
      institutionId: institutionId,
      physicalLocationId: physicalLocationId
    };

    return this.http.post<any>(
      `${this.configurationService.config.baseUrl}/WhoChecklist/GetQuestionText`, obj
    );
  }

  ensureChecklistIsLoaded(patientTheatreVisitId: number): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientChecklistAndLoad(patientTheatreVisitId).subscribe(
        () => {
          observer.next(true);
        },
        () => {
          observer.next(false);
        }
      );
    });
  }

  getPatientChecklistAndLoad(patientTheatreVisitId: number) {
    return new Observable((observer) => {
      this.http
        .get<string>(`${this.configurationService.config.baseUrl}/WhoChecklist/GetQuestionProgress/${patientTheatreVisitId}`)
        .subscribe(
          (progress) => {
            this.checklistProgressStorageService
              .addUpdateChecklistProgress(patientTheatreVisitId, progress)
              .subscribe(
                () => {
                  this.getPatientChecklistAnswersAndLoad(patientTheatreVisitId).subscribe(
                    (patientAnswers) => {
                      observer.next({ progress, patientAnswers });
                    },
                    () => {
                      observer.next(false);
                    }
                  );
                
                },
                (error) => {
                  observer.error(error);
                }
              );
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }


    ensureChecklistAnswersAreLoaded(patientTheatreVisitId: number): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientChecklistAnswersAndLoad(patientTheatreVisitId).subscribe(
        () => {
          observer.next(true);
        },
        () => {
          observer.next(false);
        }
      );
    });
  }

  getPatientChecklistAnswersAndLoad(patientTheatreVisitId: number) {
    return new Observable((observer) => {
      this.http
        .get<WhoChecklist>(`${this.configurationService.config.baseUrl}/WhoChecklist/GetQuestion/${patientTheatreVisitId}`)
        .subscribe(
          (patientAnswers) => {
            this.whoChecklistStorageService
              .updateWhoChecklist(patientAnswers)
              .subscribe(
                () => {
                  observer.next(patientAnswers);
                },
                (error) => {
                  observer.error(error);
                }
              );
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  saveChecklistAnswers(checklistProgress: WhoChecklist): Observable<any> {
    return new Observable((observer) => {
      this.whoChecklistStorageService.updateWhoChecklist(checklistProgress).subscribe(
        () => {
          this.queueService.enqueue(QueueType.ChecklistAnsweredQuestionsQueue, checklistProgress).subscribe(
            () => {
              observer.next(true);
            },
            (error) => {
              console.error('[WhoChecklistService] Error queuing checklist to local storage:', error);
              observer.error(false);
            }
          );
        },
        (error) => {
          console.error('[WhoChecklistService] Error saving checklist to local storage:', error);
          observer.error(error);
        }
      );
    });
  }

  getCheckListQuestions() {
    return new Observable<ChecklistQuestion[]>((observer) => {
      // API-first approach: Always fetch from API first
      this.http.get<ChecklistQuestion[]>(`${this.configurationService.config.baseUrl}/WhoChecklist/GetQuestions`)
        .subscribe(
          async (apiResponse) => {
            try {
              if (apiResponse && apiResponse.length > 0) {
                // Save fresh data to local storage
                await this.checklistQuestionStorageService.syncQuestionsFromApi(apiResponse);
                observer.next(apiResponse);
                observer.complete();
              } else {
                // API returned empty, fallback to local storage
                const localQuestions = await this.checklistQuestionStorageService.getAllChecklistQuestions();
                observer.next(localQuestions || []);
                observer.complete();
              }
            } catch (storageError) {
              console.error('[WhoChecklistService] Error saving questions to local storage:', storageError);
              // Still return API data even if storage fails
              observer.next(apiResponse || []);
              observer.complete();
            }
          },
          async (apiError) => {
            console.error('[WhoChecklistService] Error fetching questions from API:', apiError);
            try {
              // API failed, fallback to local storage
              const localQuestions = await this.checklistQuestionStorageService.getAllChecklistQuestions();
              if (localQuestions && localQuestions.length > 0) {
                observer.next(localQuestions);
                observer.complete();
              } else {
                observer.next([]);
                observer.complete();
              }
            } catch (storageError) {
              console.error('[WhoChecklistService] Error fetching questions from local storage:', storageError);
              observer.next([]);
              observer.complete();
            }
          }
        );
    });
  }
}